import {
    MANUFACTURERBOD_PLACEHOLDERS,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { ContentItemViewNodesType, DataNode } from '../types'
import { isManufacturerOrBODHeader } from './manufacturerBodUtils'

/**
 * Generates an order label based on the depth and index of a node.
 * Depth is 0-based:
 * - Depth 0: A., B., C.
 * - Depth 1: 1., 2., 3.
 * - Depth 2: a., b., c.
 * - Depth 3: 1), 2), 3)
 * - Depth 4: a), b), c)
 *
 * @param depth - The depth of the node in the tree (0-based).
 * @param index - The index of the node at the current depth (0-based).
 * @returns A formatted order label as a string.
 */
export const getOrderLabel = (depth: number, index: number): string => {
    if (depth < 0 || depth > 4) {
        return ''
    }

    const isAlpha = depth === 0 || depth === 2 || depth === 4
    const maxIndex = isAlpha ? 26 : 100

    if (index < 0 || index >= maxIndex) {
        return ''
    }

    switch (depth) {
        case 0:
            return `${String.fromCharCode(65 + index)}.` // A., B., C.
        case 1:
            return `${index + 1}.` // 1., 2., 3.
        case 2:
            return `${String.fromCharCode(97 + index)}.` // a., b., c.
        case 3:
            return `${index + 1})` // 1), 2), 3)
        case 4:
            return `${String.fromCharCode(97 + index)})` // a), b), c)
        default:
            return ''
    }
}

/**
 * Generates a hidden order label for a given depth.
 *
 * - For articles (depth 0): '_._'
 * - For paragraph level (depth 1): '_.'
 * - For depth 2 and deeper: '_'
 * - For manufacturer/BOD paragraphs: ''
 *
 * @param depth - The depth of the node in the tree (0-based).
 * @param isManufacturerOrBOD - (Optional) If true, returns '' for manufacturer/BOD paragraphs.
 * @returns The hidden order label as a string for the given depth.
 */
export const getHiddenOrderLabel = (
    depth: number,
    isManufacturerOrBOD?: boolean
): string => {
    if (isManufacturerOrBOD) return ''
    if (depth === 0) return '_._' // Article level
    if (depth === 1) return '_.' // Paragraph level
    return '_' // Depth 2 and deeper
}

/**
 * Returns the order label for a node based on its visibility, type, and depth.
 *
 * - For manufacturer/BOD paragraphs without placeholder text, returns an empty string (no label).
 * - For hidden nodes, returns a hidden order label (unless manufacturer/BOD, then returns '').
 * - For Article type at depth 0, returns a label based on the active tab and index.
 * - Otherwise, returns a formatted order label based on depth and index.
 *
 * @param node - The node object.
 * @param visibleNodes - Array of visible sibling nodes.
 * @param depth - The depth of the node in the tree.
 * @param activeTab - (Optional) The active tab label, used for top-level articles.
 * @param contentItemViewNodes - (Optional) Array of content item view nodes for manufacturer/BOD logic.
 * @returns The formatted order label string, or an empty string for manufacturer/BOD paragraphs without placeholder text.
 */
export const getNodeOrderLabel = (
    node: DataNode,
    visibleNodes: DataNode[],
    depth: number,
    activeTab?: number,
    contentItemViewNodes?: Partial<ContentItemViewNodesType>[]
): string => {
    const visibleIndex = visibleNodes.findIndex((n) => n.key === node.key)

    // Check if this is a manufacturer or BOD node
    let isManufacturerOrBOD = false
    let shouldSkipNumbering = false

    // Approach 1: Check using node's direct properties (production case)
    if (node.isManufacturers || node.isBOD) {
        isManufacturerOrBOD = true
        // Use centralized utility to determine if it's a header or pill
        const isHeader = isManufacturerOrBODHeader(node)
        shouldSkipNumbering = !isHeader // Skip numbering for pills, not for headers
    }
    // Approach 2: Fallback to element lookup (for backward compatibility)
    else if (contentItemViewNodes && node.contentItemNodes) {
        const allElements = node.contentItemNodes.flatMap((ci) => {
            if (!ci.elements || ci.elements.length === 0) {
                return [ci]
            }
            return ci.elements
        })

        for (const el of allElements) {
            const linkedContentItem = findContentItemByElementId(
                contentItemViewNodes,
                el.id
            )

            if (
                linkedContentItem?.isManufacturers ||
                linkedContentItem?.isBOD
            ) {
                isManufacturerOrBOD = true
                const hasPlaceholderText = linkedContentItem?.elements
                    ? hasManufacturerOrBODPlaceholderText(
                          linkedContentItem.elements
                      )
                    : false
                // Skip numbering if no placeholder text (means it's a pill, not a header)
                shouldSkipNumbering = !hasPlaceholderText
                break
            }
        }
    }

    // Skip numbering for manufacturer/BOD pills (actual manufacturers like "Armstrong Pumps, Inc")
    // But include numbering for header paragraphs (like "Manufacturers:" or "Basis-of-Design Product:")
    if (isManufacturerOrBOD && shouldSkipNumbering) {
        return ''
    }

    if (node.isHidden) {
        return getHiddenOrderLabel(
            depth,
            isManufacturerOrBOD && shouldSkipNumbering
        )
    }

    if (node.type === NODE_TYPES.Article && depth === 0 && activeTab) {
        return `${activeTab}.${visibleIndex + 1}`
    }

    return getOrderLabel(depth - 1, visibleIndex)
}

/**
 * Finds the first ContentItemViewNode in the provided array that contains an element
 * (at any nesting level) with the specified elementId.
 *
 * @param contentItemViewNodes - Array of ContentItemViewNodes to search within.
 * @param elementId - The ID of the element to find.
 * @returns The first ContentItemViewNode containing the element, or undefined if not found.
 */
const findContentItemByElementId = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    elementId?: string
): Partial<ContentItemViewNodesType> | undefined => {
    const containsElement = (elements: any[]): boolean =>
        (elements || []).some(
            (el) =>
                el.id === elementId ||
                (el.elements && containsElement(el.elements))
        )
    return contentItemViewNodes.find((node) =>
        containsElement(node.elements || [])
    )
}

/**
 * Recursively checks if any element or its nested children contain manufacturer or BOD placeholder text.
 *
 * @param elements - Array of elements to search through.
 * @returns True if any element or nested element contains manufacturer or BOD placeholder text, otherwise false.
 */
const hasManufacturerOrBODPlaceholderText = (elements: any[]): boolean => {
    return (elements || []).some(
        (el) =>
            (el.text &&
                (el.text.includes(MANUFACTURERBOD_PLACEHOLDERS.MANUFACTURER) ||
                    el.text.includes(MANUFACTURERBOD_PLACEHOLDERS.BOD1) ||
                    el.text.includes(MANUFACTURERBOD_PLACEHOLDERS.BOD2))) ||
            (el.elements && hasManufacturerOrBODPlaceholderText(el.elements))
    )
}
